import { useCallback } from "react"
import { useNavigate } from "@tanstack/react-router"

import type { UpdateThreadArgs } from "@productlane/zero/types"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { mutateOk, useZero } from "@/lib/zero"

// Each push writes an activity row and an Inngest event per thread, like the inbox's bulk done.
const MAX_THREADS_PER_CALL = 500

export interface DoneThread {
	id: string
	state: string
	title?: string | null
	snoozedUntil?: number | null
	lastStateChangeAt?: number | null
}

const iso = (ms: number | null | undefined) =>
	ms ? new Date(ms).toISOString() : null

/**
 * Marks threads done from the Notifications page with the inbox's own toasts. One thread
 * gets the inbox's undo, which also brings its notifications back through `restore`.
 */
export function useMarkThreadsDone() {
	const zero = useZero()
	const navigate = useNavigate()

	return useCallback(
		(threads: DoneThread[], restore: () => Promise<unknown>) => {
			if (threads.length === 0) return Promise.resolve()
			const at = new Date().toISOString()
			const [only] = threads
			if (threads.length === 1 && only) {
				const done = mutateOk(
					zero.mutate(
						mutators.threads.update({
							id: only.id,
							state: "PROCESSED",
							lastStateChangeAtIso: at,
						}),
					),
				)
				toast.success({
					title: "Marked done",
					descriptionAction: {
						label: only.title ?? "",
						callback: () =>
							void navigate({ to: "/threads/$id", params: { id: only.id } }),
					},
					onUndo: async () => {
						await mutateOk(
							zero.mutate(
								mutators.threads.update({
									id: only.id,
									state: only.state as UpdateThreadArgs["state"],
									snoozedUntilIso: iso(only.snoozedUntil),
									lastStateChangeAtIso: iso(only.lastStateChangeAt),
								}),
							),
						)
						await restore()
					},
				})
				return done
			}

			const ids = threads.map((t) => t.id)
			const batches: string[][] = []
			for (let i = 0; i < ids.length; i += MAX_THREADS_PER_CALL) {
				batches.push(ids.slice(i, i + MAX_THREADS_PER_CALL))
			}
			toast.success({
				title: `Marked ${ids.length} thread${ids.length > 1 ? "s" : ""} as done.`,
			})
			return Promise.all(
				batches.map((batch) =>
					mutateOk(
						zero.mutate(
							mutators.threads.updateBulk({
								ids: batch,
								state: "PROCESSED",
								lastStateChangeAtIso: at,
							}),
						),
					),
				),
			)
		},
		[zero, navigate],
	)
}
